import React, { useMemo } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import moment from "moment";
import formatAmount from "utils/formatAmount";

ChartJS.register(BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend);

const COLORS = ["#24344d", "#428db9", "#62b5ad", "#edb865", "#8d83ca", "#e58d83", "#91a6b7"];
const amount = (value) => Number(value || 0);
const label = (value) => String(value || "Unknown").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom", labels: { usePointStyle: true, padding: 16 } },
    tooltip: { callbacks: { label: (context) => `${context.dataset.label || context.label}: ${formatAmount(context.parsed?.y ?? context.parsed ?? 0)}` } },
  },
};

const barOptions = {
  ...chartOptions,
  scales: {
    x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 0 } },
    y: { beginAtZero: true, ticks: { callback: (value) => formatAmount(value) } },
  },
};

function groupRows(rows, getKey, getAmount) {
  const groups = new Map();
  rows.forEach((row) => {
    const key = getKey(row) || "Unknown";
    groups.set(key, (groups.get(key) || 0) + amount(getAmount(row)));
  });
  return [...groups].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

const card = (title, value, hint) => (
  <div key={title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <p className="text-sm font-medium text-slate-500">{title}</p>
    <p className="mt-2 text-2xl font-semibold text-slate-800 break-words">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const ChartCard = ({ title, subtitle, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm min-w-0">
    <h3 className="font-semibold text-slate-800">{title}</h3>
    <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
    <div className="mt-5 h-72">{children}</div>
  </section>
);

const AnalyticsReport = ({ reportType, result, details }) => {
  const view = useMemo(() => {
    const summary = result?.summary || {};

    if (reportType === "due_installments") {
      const rows = result?.docs || [];
      return {
        cards: [
          card("Due installments", summary.count || 0),
          card("Scheduled amount", formatAmount(summary.totalAmount || 0)),
          card("Inventories", new Set(rows.map((row) => String(row.inventory || ""))).size),
        ],
        primary: groupRows(rows, (row) => moment(row.dueDate).isValid() ? moment(row.dueDate).format("MMM YYYY") : "Unknown", (row) => amount(row.amount) - amount(row.paidAmount)),
        secondary: groupRows(rows, (row) => label(row.type), (row) => amount(row.amount) - amount(row.paidAmount)),
        primaryTitle: "Outstanding by due month",
        secondaryTitle: "Outstanding by installment type",
      };
    }

    if (reportType === "sold_inventories") {
      const rows = result?.docs || [];
      return {
        cards: [
          card("Sold inventories", summary.count || 0),
          card("Total selling price", formatAmount(summary.totalSellingPrice || 0)),
          card("Sectors", new Set(rows.map((row) => String(row.sector || ""))).size),
        ],
        primary: groupRows(rows, (row) => row.sectorTitle, (row) => row.sellingPrice),
        secondary: groupRows(rows, (row) => label(row.paymentType), () => 1),
        primaryTitle: "Selling price by sector",
        secondaryTitle: "Inventories by payment type",
      };
    }

    if (reportType === "payments_received") {
      const rows = summary.byMethod || [];
      return {
        cards: [
          card("Total received", formatAmount(summary.totalAmount || 0)),
          card("Net cash received", formatAmount(summary.netCashReceived || 0)),
          card("Settlement amount", formatAmount(summary.otherAmount || 0)),
          card("Payment methods", rows.length),
        ],
        primary: rows.map((row) => ({ name: label(row.method), value: amount(row.amountTotal) })),
        secondary: rows.map((row) => ({ name: label(row.method), value: amount(row.paymentCount) })),
        primaryTitle: "Amount by payment method",
        secondaryTitle: "Receipts by payment method",
      };
    }

    const rows = result?.bySector || [];
    return {
      cards: [
        card("Total coming", formatAmount(summary.totalComingAmount || 0)),
        card("Pending till today", formatAmount(summary.totalPendingAmountTillToday || 0)),
        card("Upcoming", formatAmount(summary.totalFutureAmount || 0)),
        card("Installments", summary.totalInstallments || 0),
      ],
      primary: rows.map((row) => ({ name: row.sectorTitle || "Unknown", value: amount(row.totalComingAmount) })),
      secondary: [
        { name: "Pending", value: amount(summary.totalPendingAmountTillToday) },
        { name: "Upcoming", value: amount(summary.totalFutureAmount) },
      ],
      primaryTitle: "Expected amount by sector",
      secondaryTitle: "Pending and upcoming",
    };
  }, [reportType, result]);

  const primary = view.primary.slice(0, 10);
  const secondary = view.secondary.filter((item) => item.value > 0);
  const hasData = primary.length > 0 || secondary.length > 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">{view.cards}</div>
      {hasData ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ChartCard title={view.primaryTitle} subtitle={primary.length < view.primary.length ? "Top 10 groups by amount" : "Based on the selected filters"}>
            <Bar options={barOptions} data={{
              labels: primary.map((item) => item.name),
              datasets: [{ label: "Amount", data: primary.map((item) => item.value), backgroundColor: COLORS[1], borderRadius: 6 }],
            }} />
          </ChartCard>
          <ChartCard title={view.secondaryTitle} subtitle="Based on the selected filters">
            {secondary.length ? (
              <Doughnut options={{ ...chartOptions, cutout: "66%" }} data={{
                labels: secondary.map((item) => item.name),
                datasets: [{ label: "Value", data: secondary.map((item) => item.value), backgroundColor: secondary.map((_, index) => COLORS[index % COLORS.length]), borderWidth: 0 }],
              }} />
            ) : <p className="text-sm text-slate-500">No chart data for these filters.</p>}
          </ChartCard>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">No chart data for these filters.</div>
      )}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">Detailed results</h3>
            <p className="text-sm text-slate-500">The rows behind these charts</p>
          </div>
        </div>
        {details}
      </section>
    </div>
  );
};

export default AnalyticsReport;
